import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const nutrients = [
  { name: 'Protein',       per100g: '27 g',    dv: 54,  dvLabel: '54% DV',  color: 'bg-green-500' },
  { name: 'Iron',          per100g: '28 mg',   dv: 100, dvLabel: '156% DV', color: 'bg-red-400'   },
  { name: 'Vitamin A',     per100g: '378 mcg', dv: 42,  dvLabel: '42% DV',  color: 'bg-amber-400' },
  { name: 'Vitamin C',     per100g: '51.7 mg', dv: 57,  dvLabel: '57% DV',  color: 'bg-lime-500'  },
  { name: 'Calcium',       per100g: '185 mg',  dv: 14,  dvLabel: '14% DV',  color: 'bg-teal-400'  },
  { name: 'Potassium',     per100g: '259 mg',  dv: 6,   dvLabel: '6% DV',   color: 'bg-green-400' },
  { name: 'Magnesium',     per100g: '147 mg',  dv: 35,  dvLabel: '35% DV',  color: 'bg-emerald-500'},
  { name: 'Antioxidants',  per100g: 'Very High', dv: 90, dvLabel: 'Exceptional', color: 'bg-green-600'},
];

const comparisons = [
  {
    nutrient: 'Vitamin C',
    multiplier: '7×',
    vsFood: 'Oranges',
    icon: 'ri-sun-line',
    color: 'bg-amber-50 border-amber-200',
    accent: 'text-amber-600',
    bar: 'bg-amber-400',
    moringaPct: 87,
    foodPct: 13,
    image: 'https://public.readdy.ai/ai/img_res/d6c10104c99c7a9dcbd3e46017d7750a.jpg',
  },
  {
    nutrient: 'Calcium',
    multiplier: '4×',
    vsFood: 'Milk',
    icon: 'ri-drop-line',
    color: 'bg-green-50 border-green-200',
    accent: 'text-green-600',
    bar: 'bg-green-500',
    moringaPct: 80,
    foodPct: 20,
    image: 'https://readdy.ai/api/search-image?query=Fresh%20cold%20glass%20of%20white%20milk%20next%20to%20green%20moringa%20powder%20on%20a%20white%20marble%20surface%2C%20calcium%20nutrition%20comparison%2C%20clean%20minimalist%20food%20photography%2C%20natural%20lighting%2C%20health%20supplement&width=280&height=180&seq=compare-calcium-milk-2026&orientation=landscape',
  },
  {
    nutrient: 'Potassium',
    multiplier: '3×',
    vsFood: 'Bananas',
    icon: 'ri-heart-pulse-line',
    color: 'bg-lime-50 border-lime-200',
    accent: 'text-lime-700',
    bar: 'bg-lime-500',
    moringaPct: 75,
    foodPct: 25,
    image: 'https://readdy.ai/api/search-image?query=Fresh%20yellow%20ripe%20bananas%20beside%20a%20scoop%20of%20green%20moringa%20powder%20on%20a%20light%20neutral%20background%2C%20potassium%20comparison%2C%20natural%20food%20photography%2C%20clean%20and%20healthy%2C%20flat%20lay%20composition&width=280&height=180&seq=compare-potassium-banana-2026&orientation=landscape',
  },
  {
    nutrient: 'Iron',
    multiplier: '3×',
    vsFood: 'Spinach',
    icon: 'ri-seedling-line',
    color: 'bg-red-50 border-red-200',
    accent: 'text-red-500',
    bar: 'bg-red-400',
    moringaPct: 75,
    foodPct: 25,
    image: 'https://readdy.ai/api/search-image?query=Fresh%20dark%20green%20spinach%20leaves%20next%20to%20vibrant%20green%20moringa%20powder%20in%20a%20bowl%2C%20iron%20mineral%20nutrition%20comparison%2C%20clean%20white%20background%2C%20natural%20organic%20food%20photography%2C%20healthy%20eating%20concept&width=280&height=180&seq=compare-iron-spinach-2026&orientation=landscape',
  },
];

const servingSuggestions = [
  { icon: 'ri-cup-line',      title: 'Morning Smoothie',  tip: '1 tsp in a fruit smoothie' },
  { icon: 'ri-bowl-line',     title: 'Soup & Stew',       tip: 'Stir in after cooking' },
  { icon: 'ri-water-flash-line', title: 'Moringa Tea',    tip: 'Mix in warm water + honey' },
  { icon: 'ri-bread-line',    title: 'Baking',            tip: 'Add to bread or porridge' },
];

export default function MoringaNutrition() {
  const [activeTab, setActiveTab] = useState<'label' | 'compare'>('label');
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center bg-green-50 text-green-700 text-xs font-semibold px-4 py-1.5 rounded-full border border-green-200 uppercase tracking-widest mb-4">
            <i className="ri-microscope-line mr-2"></i>Nutritional Transparency
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What&apos;s in Our <span className="text-green-600">Moringa Powder?</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Pure, lab-verified nutrients per 100g serving. No fillers, no additives — just moringa exactly as nature made it.
          </p>
          {/* Dedicated page link */}
          <button
            onClick={() => navigate('/products/moringa-powder')}
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 px-5 py-2.5 rounded-full hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-star-line"></i>
            View the full Pure Moringa Powder product page
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 border border-gray-200 rounded-full px-1 py-1">
            <button
              onClick={() => setActiveTab('label')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'label' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}
            >
              <i className="ri-file-list-3-line mr-2"></i>Nutrition Facts
            </button>
            <button
              onClick={() => setActiveTab('compare')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'compare' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}
            >
              <i className="ri-bar-chart-2-line mr-2"></i>Compare to Other Foods
            </button>
          </div>
        </div>

        {/* ── NUTRITION FACTS TAB ── */}
        {activeTab === 'label' && (
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left — product photo + serving info */}
            <div>
              <div className="rounded-2xl overflow-hidden w-full h-72 mb-6">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_e17d2c62eadfd03030c4db97e8f52ea6_45dfadd7.jpg"
                  alt="Pure Moringa Powder — MY AVA Farms Ghana"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Serving size card */}
              <div className="bg-green-50 border border-green-100 rounded-2xl p-5 mb-6">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-3">Recommended Daily Serving</p>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-extrabold text-green-700">1–2</p>
                    <p className="text-xs text-gray-500">teaspoons</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-extrabold text-green-700">3–6g</p>
                    <p className="text-xs text-gray-500">per day</p>
                  </div>
                  <div className="flex-1 text-sm text-gray-600 leading-relaxed border-l border-green-200 pl-4">
                    Safe for daily use. Add to food or drinks. Avoid boiling to preserve nutrients.
                  </div>
                </div>
              </div>

              {/* Serving suggestions */}
              <div className="grid grid-cols-2 gap-3">
                {servingSuggestions.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="w-8 h-8 flex items-center justify-center text-green-600 shrink-0">
                      <i className={`${s.icon} text-xl`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-800">{s.title}</p>
                      <p className="text-xs text-gray-400">{s.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — nutrition label + bars */}
            <div>
              {/* Formal label header */}
              <div className="border-2 border-gray-900 rounded-2xl overflow-hidden">
                <div className="bg-gray-900 text-white px-6 py-4">
                  <p className="text-2xl font-extrabold tracking-tight">Nutrition Facts</p>
                  <p className="text-sm text-gray-300 mt-0.5">Serving size: 3g (1 teaspoon) · Per 100g shown below</p>
                </div>

                <div className="px-6 py-2 border-b-4 border-gray-900 bg-white">
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider py-2">% Daily Value based on 2,000 cal diet</p>
                </div>

                <div className="bg-white divide-y divide-gray-100">
                  {nutrients.map((n, i) => (
                    <div key={i} className="px-6 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{n.name}</span>
                          <span className="text-xs text-gray-400">{n.per100g}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n.dv >= 100 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {n.dvLabel}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className={`${n.color} h-2 rounded-full transition-all duration-700`}
                          style={{ width: `${Math.min(n.dv, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-green-50 border-t-2 border-green-200 px-6 py-4">
                  <p className="text-xs text-green-700 font-medium leading-relaxed">
                    <i className="ri-information-line mr-1"></i>
                    All values are for dried moringa leaf powder. Iron marked 156% DV makes moringa an exceptional source for combating anaemia. Values may vary slightly by batch.
                  </p>
                </div>
              </div>

              {/* Quality badge row */}
              <div className="flex flex-wrap gap-3 mt-6">
                {['100% Pure', 'No Additives', 'Low-Heat Processed', 'Locally Grown', '24-Month Shelf Life'].map((badge, i) => (
                  <span key={i} className="inline-flex items-center text-xs font-semibold bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full">
                    <i className="ri-check-line mr-1.5"></i>{badge}
                  </span>
                ))}
              </div>

              <a
                href="https://paystack.shop/pay/v9-4hu3n50"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-bag-line text-lg"></i>Order Pure Moringa Powder
              </a>
            </div>
          </div>
        )}

        {/* ── COMPARE TAB ── */}
        {activeTab === 'compare' && (
          <div>
            <p className="text-center text-gray-500 text-sm mb-10 max-w-xl mx-auto">
              Moringa is one of the most nutrient-dense plants on earth. Here&apos;s how it stacks up against everyday foods you already know.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {comparisons.map((c, i) => (
                <div key={i} className={`rounded-2xl border overflow-hidden hover:-translate-y-1 transition-transform duration-300 ${c.color}`}>
                  <div className="w-full h-36 overflow-hidden">
                    <img src={c.image} alt={`Moringa vs ${c.vsFood}`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{c.nutrient}</p>
                    <div className="flex items-end gap-2 mb-3">
                      <span className={`text-4xl font-extrabold ${c.accent}`}>{c.multiplier}</span>
                      <span className="text-sm text-gray-600 mb-1">more than {c.vsFood}</span>
                    </div>

                    {/* Comparison bar */}
                    <div className="space-y-2 mb-1">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-semibold text-gray-800">Our Moringa</span>
                          <span className={`font-bold ${c.accent}`}>{c.moringaPct}%</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2">
                          <div className={`${c.bar} h-2 rounded-full`} style={{ width: `${c.moringaPct}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">{c.vsFood}</span>
                          <span className="text-gray-400">{c.foodPct}%</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2">
                          <div className="bg-gray-300 h-2 rounded-full" style={{ width: `${c.foodPct}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA strip */}
            <div className="bg-green-600 rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-white">
              <div>
                <p className="text-xl font-bold mb-1">Ready to add this superfood to your daily routine?</p>
                <p className="text-green-100 text-sm">Available in 50g · 100g · 250g · 1kg — grown &amp; processed right here in Ghana.</p>
              </div>
              <a
                href="https://paystack.shop/pay/v9-4hu3n50"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-green-700 hover:bg-gray-100 font-bold px-8 py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-shopping-bag-line mr-2"></i>Order Now
              </a>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
